import React, { useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import classNames from 'classnames';

import {
  CoreLink,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
} from '@uncinc/react-drupal-core';

import {
  FormikWrapper,
  FormTextField,
  FormCheckbox,
  Button,
  Container,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

import type { FormikProps } from 'formik';

import { formatCartItemsForAnalytics } from '../../../helpers/commerce';
import { prefixObjectKeys } from '../../../helpers';
import {
  ROUTE_ACCOUNT_LOGIN,
  ROUTE_CHECKOUT_V2,
  ROUTE_CUSTOMER_SERVICE,
} from '../../../config/routes';
import {
  APP_OPTIONS,
  DEFAULT_LANGUAGE,
  MINIMUM_PASSWORD_CHARACTER_COUNT,
  URL_SSO_FLOW_IDENTIFIER,
  APPLICATON_VNL_ID,
  SSO_APPLICATON_RISK_MONITOR_ID,
} from '../../../config/constants';
import {
  ProfileForm,
  getProfileValidationSchema,
} from '../../../components/ProfileForm';
import InformationOverlay from '../../../components/InformationOverlay';
import useRegistrationFlow from './useRegistrationFlow';

import LogoImageVNL from '../../../assets/images/VNL_logo_clean.svg';

import type { RegistrationFormValues, ApplicationId } from './types';

import './style.scss';

const PAGE_TITLE = 'Registreer | VeiligheidNL';

/**
 * User registration page component.
 *
 * Handles new user registration with:
 * - Email and password fields
 * - Profile information (via ProfileForm component)
 * - Privacy terms acceptance
 * - Spotler newsletter subscription
 * - GTM analytics tracking
 * - SSO flow support (Risk Monitor)
 */
const UserRegister: React.FC = () => {
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<RegistrationFormValues> | null>(null);

  // Parse URL parameters
  const urlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );

  const redirectUrl = useMemo(() => {
    const destination = urlParams.get('destination');
    if (destination) {
      return `${destination}${window.location.search}`;
    }
    return null;
  }, [urlParams]);

  const entryNid = urlParams.get('nid');
  const isLoginWallRegister = urlParams.get('source') === 'login_wall';
  const userParamValues = useMemo(
    () => Object.fromEntries(urlParams),
    [urlParams],
  );
  const isSSORegister = urlParams.get(URL_SSO_FLOW_IDENTIFIER) !== null;

  let activeApplication = APPLICATON_VNL_ID;
  const clientId = urlParams.get('client_id') || '';
  const ssoClient =
    clientId &&
    APP_OPTIONS.ssoServices.find((s) => s.clientIds?.includes(clientId));
  if (ssoClient) {
    activeApplication = ssoClient.id;
  }

  // Redux selectors for cart data
  const totalPrice = useSelector(
    (state: Immutable.Map<string, unknown>) => state.getIn(
      [
        'commerce',
        'cart',
        'content',
        'attributes',
        'order_total',
        'subtotal',
        'number',
      ],
      0,
    ) as number,
  );

  const analyticsItems = useSelector(
    (state: Immutable.Map<string, unknown>) => formatCartItemsForAnalytics(
      state.getIn(['commerce', 'cart', 'products']) as
      | Immutable.List<unknown>
      | undefined,
    ),
  );

  // Use custom hook for registration flow
  const { userRegisterStatus, handleSubmit } = useRegistrationFlow({
    pageTitle: PAGE_TITLE,
    redirectUrl,
    entryNid,
    isLoginWallRegister,
    isSSORegister,
    totalPrice,
    analyticsItems,
  });

  // Validation schema - not memoized because getProfileValidationSchema() uses hooks internally
  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t('form.register.email.invalid'))
      .required(t('form.register.email.required')),
    passwordRegister: Yup.string()
      .min(
        MINIMUM_PASSWORD_CHARACTER_COUNT,
        t('form.register.password.minimum', {
          count: MINIMUM_PASSWORD_CHARACTER_COUNT,
        }),
      )
      .matches(
        /^(?=.*\d)(?=.*[a-zA-Z]).*$/,
        t('form.register.password.invalid'),
      )
      .required(t('form.register.password.required')),
    passwordRegisterConfirm: Yup.string()
      .oneOf(
        [Yup.ref('passwordRegister'), null],
        t('form.register.password.no_match'),
      )
      .required(t('form.register.password.required')),
    // Load in profile fields
    ...getProfileValidationSchema(),
    privacy_terms: Yup.boolean().required(
      t('form.register.privacy_required'),
    ),
  });

  // Handler for touching select fields to show validation errors
  // Temp fix: https://uncinc.monday.com/boards/1674548856/pulses/9431098326
  const handleButtonClick = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    formRef.current?.setFieldTouched('field_work_area');
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    formRef.current?.setFieldTouched('field_job_title');
  }, []);

  // Determine if email field should be disabled
  const isEmailDisabled = useMemo(() => !!(
    userParamValues.email &&
    (isSSORegister || urlParams.get('destination') === ROUTE_CHECKOUT_V2)
  ), [isSSORegister, urlParams, userParamValues.email]);

  // Button class names
  const buttonClassName = classNames('Button', 'Button--primary', {
    'Button--loading': userRegisterStatus === UI_OPERATION_STATUS_REQUEST,
  });

  return (
    <>
      <Helmet htmlAttributes={{ lang: DEFAULT_LANGUAGE }}>
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <Container small>
        <div className="FormPage PageRegister PageMinimal" id="main">
          <div className="PageMinimal__top">
            <CoreLink className="Logo" href="/">
              <img src={LogoImageVNL} alt="VeiligheidNL" />
            </CoreLink>
            <h1>{t(`form.register.${activeApplication}_title`)}</h1>
          </div>
          <div className="PageMinimal__content">
            {userRegisterStatus === UI_OPERATION_STATUS_ERROR && (
              <div className="Form__error">
                {t('form.register.email.exists')}
                <CoreLink
                  href={ROUTE_ACCOUNT_LOGIN}
                  className="Form__error__link"
                >
                  {t('form.register.email.exists_login_link')}
                </CoreLink>
              </div>
            )}
            <FormikWrapper
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              initialValues={prefixObjectKeys('field_', userParamValues, [
                'first_name',
                'last_name',
                'infix_name',
              ])}
              key={Object.keys(userParamValues).length}
              innerRef={formRef}
            >
              {activeApplication === SSO_APPLICATON_RISK_MONITOR_ID && (
                <div className="FormElement TextField OverlayFormElement">
                  <label htmlFor="email">{t('login.email.label')}</label>
                  <InformationOverlay
                    overlayKey="register_risk_monitor_email_info_overlay"
                    title={t('form.register_risk_monitor.overlay_title')}
                    body={t('form.register_risk_monitor.overlay_body')}
                  />
                </div>
              )}

              <FormTextField
                title={
                  activeApplication !== SSO_APPLICATON_RISK_MONITOR_ID
                    ? t('login.email.label')
                    : ''
                }
                fieldName="email"
                type="email"
                disabled={isEmailDisabled}
                required
                data-lpignore
                autocomplete="off"
              />

              {/* Load in the profile form fields */}
              <ProfileForm formRef={formRef} />

              <FormTextField
                title={t('login.password.label')}
                fieldName="passwordRegister"
                type="password"
                className="InputPassword"
                showPasswordReveal
                required
                data-lpignore
                autocomplete="off"
              />
              <FormTextField
                title={t('form.register.password.confirm')}
                fieldName="passwordRegisterConfirm"
                type="password"
                className="InputPassword"
                showPasswordReveal
                required
                data-lpignore
                autocomplete="off"
              />
              <WysiwygField
                property="schema:text"
                html={t('form.register.privacy_text')}
              />

              <div className="WebformCheckboxes privacy">
                <FormCheckbox
                  title={t('form.register.privacy_label')}
                  fieldName="privacy_terms"
                  required
                />
              </div>

              <div className="Form__actions">
                <Button
                  type="submit"
                  onClick={handleButtonClick}
                  className={buttonClassName}
                >
                  {t('form.register.button')}
                  <i className="icon icon-arrow-right" />
                </Button>
                {activeApplication === APPLICATON_VNL_ID && (
                  <a
                    href={`/login${window.location.search}`}
                    className="LoginButton"
                  >
                    <span>{t('login.register_button')}</span>
                  </a>
                )}
              </div>
            </FormikWrapper>
          </div>
          <div className="PageMinimal__usp">
            <div className="PageMinimal__usp__link">
              <CoreLink
                href={ROUTE_CUSTOMER_SERVICE}
                target="_blank"
                className="Button Button--secondary Button--help"
              >
                {t('general.need_help')}
              </CoreLink>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default UserRegister;
